/**
 * FeatureDetailModal Component
 *
 * Modal dialog showing detailed information about a feature including evidence and file paths
 */

import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { FileCode, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Feature, FeatureCategory } from '@forgenetwork/types';

interface FeatureDetailModalProps {
  feature: Feature | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Category-specific styling for feature badges
 */
const categoryColors: Record<FeatureCategory, string> = {
  authentication: 'bg-red-100 text-red-800',
  authorization: 'bg-pink-100 text-pink-800',
  'data-storage': 'bg-green-100 text-green-800',
  api: 'bg-blue-100 text-blue-800',
  ui: 'bg-purple-100 text-purple-800',
  'business-logic': 'bg-orange-100 text-orange-800',
  integration: 'bg-yellow-100 text-yellow-800',
  infrastructure: 'bg-gray-100 text-gray-800',
  testing: 'bg-cyan-100 text-cyan-800',
  other: 'bg-indigo-100 text-indigo-800',
};

/**
 * Complexity badge variants
 */
const complexityVariants: Record<string, 'default' | 'secondary' | 'destructive'> = {
  low: 'secondary',
  medium: 'default',
  high: 'destructive',
};

/**
 * Format category name for display
 */
function formatCategory(category: FeatureCategory): string {
  return category
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * FeatureDetailModal - Shows comprehensive feature information
 */
export function FeatureDetailModal({
  feature,
  open,
  onOpenChange,
}: FeatureDetailModalProps): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);

  // Reset copied state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setCopied(false);
    }
  }, [open]);

  if (!feature) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>No Feature Selected</DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const confidencePercentage = Math.round(feature.confidence * 100);

  const handleCopyJSON = async (): Promise<void> => {
    try {
      const json = JSON.stringify(feature, null, 2);
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold mb-2">
                {feature.name}
              </DialogTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  className={cn(
                    'font-medium',
                    categoryColors[feature.category]
                  )}
                >
                  {formatCategory(feature.category)}
                </Badge>
                <Badge variant={complexityVariants[feature.complexity]}>
                  {feature.complexity.toUpperCase()} Complexity
                </Badge>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyJSON}
              className="shrink-0"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy JSON
                </>
              )}
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Description */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">
              Description
            </h3>
            <p className="text-sm">{feature.description}</p>
          </section>

          {/* Confidence Score */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
              Confidence Score
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Detection Confidence</span>
                <span className="font-medium">{confidencePercentage}%</span>
              </div>
              <Progress value={confidencePercentage} className="h-2" />
            </div>
          </section>

          {/* Evidence / Signals */}
          {feature.evidence && feature.evidence.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                Detection Evidence
              </h3>
              <ul className="space-y-2">
                {feature.evidence.map((evidence, index) => (
                  <li
                    key={index}
                    className="text-sm bg-muted p-3 rounded-md border"
                  >
                    {evidence}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Associated Files */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
              Associated Files ({feature.files.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {feature.files.map((filePath, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-sm p-2 bg-muted rounded-md border hover:bg-accent transition-colors"
                >
                  <FileCode className="h-4 w-4 text-muted-foreground shrink-0" />
                  <code className="flex-1 font-mono text-xs break-all">
                    {filePath}
                  </code>
                </div>
              ))}
            </div>
          </section>

          {/* Metadata */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
              Metadata
            </h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted">
                <span className="text-sm text-muted-foreground">Feature ID</span>
                <code className="text-xs font-mono">{feature.id}</code>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted">
                <span className="text-sm text-muted-foreground">Category</span>
                <span className="text-sm font-medium">
                  {formatCategory(feature.category)}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted">
                <span className="text-sm text-muted-foreground">Complexity</span>
                <span className="text-sm font-medium capitalize">
                  {feature.complexity}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted">
                <span className="text-sm text-muted-foreground">File Count</span>
                <span className="text-sm font-medium">{feature.files.length}</span>
              </div>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
